"use client";

import { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  AGENT_BUDGETS_ANCHOR,
  AGENT_MONTH_CARD_RAISE,
  AGENT_MONTH_CARD_STOP,
  AGENT_MONTH_CARD_TITLE,
  AGENT_MONTH_SPENT_GENERIC,
  ANALYTICS_EVENTS,
  type TurnOutcome,
} from "@bash/shared";
import { posthog } from "@/lib/posthog";
import { useConnectionStore } from "@/stores/connectionStore";
import { useCreditStore } from "@/stores/creditStore";
import { useTaskStore } from "@/stores/taskStore";
import { resendLastUserMessage, focusComposer } from "@/lib/retry-turn";
import { ErrorIcon } from "@/components/icons/ErrorIcon";
import { ResolvedCheckIcon } from "@/components/icons/ResolvedCheckIcon";
import { useAgentMonthTitle } from "@/components/team/useAgentMonthTitle";
import { isAgentMonthRefusal } from "@/lib/agent-month";

interface ErrorBlockProps {
  error: string;
  code?: string;
  /** @deprecated Legacy string category. Use `outcome` instead. */
  category?: string;
  /**
   * Structured TurnOutcome from the sandbox's classifyTermination(). When
   * present, the UI variant comes from `outcome.kind` — legacy `category`
   * is only consulted as a fallback for in-flight transcripts.
   */
  outcome?: TurnOutcome;
  /**
   * The task this block belongs to, threaded from ContentBlockRenderer
   * (messages only render for the selected task, so the selected id IS the
   * owning id — see the T9 note there). The out-of-credits card uses it to
   * resume the SAME task via checkpoint:continue once the balance rises.
   */
  taskId?: string | null;
}

type ActionType =
  | "retry"
  | "settings"
  | "credits"
  | "continue"
  | "stop"
  | "support"
  | "new_task"
  | "compact_continue"
  | "fork_new_task"
  | "resume"
  | "guardrails"
  | "agent_budget";
type Severity = "error" | "warning" | "info";

interface ErrorVariant {
  severity: Severity;
  title: string;
  description: string;
  /**
   * Optional: a variant may honestly have NOTHING for the user to do. The
   * `detached` card is the case — the turn may still be running, so every
   * available action (retry above all) would be a worse lie than silence.
   */
  primaryAction?: { label: string; action: ActionType };
  secondaryAction?: { label: string; action: ActionType };
  tertiaryAction?: { label: string; action: ActionType };
}

const DESCRIPTION_TRUNCATE_AT = 200;

/**
 * Classify an error into "structural" (deterministic — retry won't help) vs
 * "transient" (server-side flake — retry might help). Drives the `unknown`
 * outcome variant so the UI offers "Edit your request" instead of "Try again"
 * for HTTP 4xx and other clearly-deterministic failures.
 *
 * Source signal:
 *  - `code` first: HTTP-style numeric codes (400/403/404 → structural,
 *    5xx → transient). This is the strong signal — when the sandbox
 *    captured a structured tool error code (Composio status_code), it
 *    rides through here.
 *  - `error` text fallback: legacy AGENT_ERROR strings without codes.
 *    A small set of phrases ("not supported", "unauthorized", etc.) is
 *    enough to catch the user-facing structural copy.
 */
export type ErrorCategory = "structural" | "transient" | "unknown";

export function classifyError(code?: string, error?: string): ErrorCategory {
  if (code) {
    const n = parseInt(code, 10);
    if (Number.isFinite(n) && String(n) === code.trim()) {
      if (n >= 400 && n < 500) return "structural"; // client-side, retry won't help
      if (n >= 500) return "transient"; // server-side, retry might help
    }
  }
  if (
    error &&
    /not supported|invalid (?:doc|file|format|request|argument)|unauthorized|forbidden|permission denied|access denied|bad request/i.test(
      error
    )
  ) {
    return "structural";
  }
  return "unknown";
}

/**
 * When a rate limit lifts, as a phrase the cards drop straight into a sentence
 * ("It works again <phrase>.").
 *
 * A reset on ANOTHER day names that day. Plan windows run to seven days, and a
 * bare clock ("at 12:00 PM") reads as later today — the user would wait for a
 * time that is not coming. A same-day reset stays a bare clock.
 */
function formatResetWhen(resetsAtSeconds: number): string {
  const resets = new Date(resetsAtSeconds * 1000);
  const clock = resets.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  if (resets.toDateString() === new Date().toDateString()) return `at ${clock}`;
  const day = resets.toLocaleDateString([], {
    month: "short",
    day: "numeric",
  });
  return `on ${day} at ${clock}`;
}

/**
 * Resolve the UI variant from a structured TurnOutcome. Source of truth —
 * callers MUST prefer this over `getLegacyVariant` when an outcome is
 * present.
 *
 * Keep in sync with branding-termination-badges.html preview.
 */
function getOutcomeVariant(
  outcome: TurnOutcome,
  errorText: string,
  code?: string,
  agentMonthTitle = AGENT_MONTH_CARD_TITLE("Bash")
): ErrorVariant | null {
  switch (outcome.kind) {
    case "completed":
    case "deferred":
      // Non-error kinds — caller shouldn't render the badge at all.
      return null;
    case "refused": {
      // A refusal with text renders as a normal assistant reply (the model's own
      // words) — no badge needed, so return null and let the text block show it.
      // But a refusal with EMPTY text would otherwise render NOTHING (a silent
      // blank). Surface a quiet "request was declined" notice so the turn is
      // never empty. `refused.text` is the model's refusal copy.
      const refusalText =
        typeof outcome.text === "string" ? outcome.text.trim() : "";
      if (refusalText) return null;
      return {
        severity: "info",
        title: "Request was declined",
        description:
          "The model declined to respond to this request. Try rephrasing it.",
        primaryAction: { label: "Edit your request", action: "fork_new_task" },
      };
    }
    case "max_turns":
      return {
        severity: "warning",
        title: "Turn limit reached",
        description:
          "This task hit its step limit. The agent can pick up right where it left off.",
        primaryAction: { label: "Keep going", action: "continue" },
        secondaryAction: { label: "That's enough", action: "stop" },
      };
    case "max_budget": {
      // Three scenarios produce a `max_budget` outcome:
      //   1. The user set a per-task spending limit and the agent crossed it
      //      → existing "Adjust limit" CTA is the right call.
      //   2. The user's remaining credit balance is the binding cap.
      //      → "Recharge credits" CTA is correct; "Adjust limit" would be
      //         misleading (there's nothing to adjust).
      //   3. THIS TURN hit the per-turn spend ceiling (the 2026-08-24 runaway
      //      guard). The balance is fine and nothing was overspent, so both
      //      CTAs above would be wrong — the turn just needs picking up.
      //   4. The DAY's spend limit was reached, so the turn never opened at all.
      //      Time alone clears this one, which is why it is the only card here
      //      that names a clock as well as a guardrail.
      //   5. The AGENT's monthly budget (agent wallet) is used up: the agent's
      //      AI usage plus its purchases reached the figure the person set.
      //      The wallet is fine; the door is the budget, never a top-up.
      // Sandbox sets `triggeredBy` to label which one. Undefined falls back
      // to 'credits' (the oldest path) for forward compatibility.
      const triggeredBy = outcome.triggeredBy ?? "credits";
      if (triggeredBy === "turn_ceiling") {
        return {
          severity: "warning",
          title: "Per-turn spend limit reached",
          description:
            "This turn reached the per-turn spend limit and stopped. Send a message to continue, or raise the limit in your guardrails.",
          primaryAction: { label: "Keep going", action: "resume" },
          secondaryAction: { label: "Adjust the limit", action: "guardrails" },
        };
      }
      if (triggeredBy === "daily_ceiling") {
        return {
          severity: "warning",
          title: "Today's model spend limit reached",
          // Narrow on purpose: the governor counts model spend, not tool or
          // compute charges, so "everything running today" over-promised.
          description:
            "Your tasks have reached today's model spend limit, so this one didn't start. It resets at midnight UTC, or you can raise the limit in your guardrails.",
          primaryAction: { label: "Adjust the limit", action: "guardrails" },
          secondaryAction: { label: "That's enough", action: "stop" },
        };
      }
      if (triggeredBy === "agent_month") {
        return {
          severity: "warning",
          title: agentMonthTitle,
          // Only the server's refusal names the agent and the month. An engine
          // trip sends no block, and the one the web synthesizes carries the
          // contract's generic label, so the structured code decides, never
          // whether there is text: anything but the refusal reads the generic line.
          description:
            (isAgentMonthRefusal(code, outcome) && errorText.trim()) ||
            AGENT_MONTH_SPENT_GENERIC,
          primaryAction: {
            label: AGENT_MONTH_CARD_RAISE,
            action: "agent_budget",
          },
          secondaryAction: { label: AGENT_MONTH_CARD_STOP, action: "stop" },
        };
      }
      if (triggeredBy === "task_limit") {
        return {
          severity: "warning",
          title: "Task budget reached",
          description:
            "This task crossed the per-task budget limit you set. You can continue in a new message or adjust the limit.",
          primaryAction: { label: "Continue", action: "retry" },
          secondaryAction: { label: "Adjust limit", action: "settings" },
        };
      }
      return {
        severity: "warning",
        title: "Out of credits",
        description:
          "You've used up your available credits and the agent paused mid-task. Top up to keep going.",
        primaryAction: { label: "Recharge credits", action: "credits" },
        secondaryAction: { label: "That's enough", action: "stop" },
      };
    }
    case "max_structured_output_retries":
      return {
        severity: "warning",
        title: "Formatting issue",
        description:
          "The agent had trouble formatting its response. Try rephrasing your request.",
        primaryAction: { label: "Try again", action: "retry" },
      };
    case "user_aborted":
      return {
        severity: "info",
        title:
          outcome.abortPhase === "tools" ? "Stopped during a tool" : "Stopped",
        description:
          outcome.abortPhase === "tools"
            ? "The tool may have partially run before you stopped it. Check the output above before continuing."
            : "You stopped this mid-response.",
        primaryAction: { label: "Continue", action: "retry" },
      };
    case "hook_blocked":
      // Every hook_blocked is a stop a policy hook made (a Stop hook's block, or a
      // PermissionRequest hook's interrupt: hookKind 'other'), never a pause: the
      // turn is over and nothing resumes on its own. The hook's words lead.
      return {
        severity: "info",
        title: "Blocked by policy",
        description: outcome.stopReason || "A policy hook stopped the agent.",
        primaryAction: { label: "Continue", action: "retry" },
      };
    case "task_cap":
      // Server-minted active-task-cap refusal (message-handlers gate). The
      // message carries the live cap number, so it leads; retry works once a
      // task finishes or is stopped.
      return {
        severity: "warning",
        title: "Task limit reached",
        description:
          outcome.message ||
          "You have too many tasks running. Wait for some to finish, or stop one, before starting more.",
        primaryAction: { label: "Try again", action: "retry" },
      };
    case "detached":
      // The Stop button could not reach the agent. Say exactly that, and say what
      // resolves it — the real terminal arriving over the relay settles this task
      // on its own (late-terminal acceptance). No CTA: the turn may still be
      // running, so "Try again" would start a second one against a live turn.
      return {
        severity: "info",
        title: "Stop not confirmed",
        description:
          "We could not reach the agent to confirm it stopped, so it may still be finishing. This task will update on its own when the agent reports back.",
      };
    case "context_exceeded":
      return {
        severity: "warning",
        title: "Conversation too long",
        description:
          "We've filled the context window. Pick how you'd like to keep going:",
        primaryAction: {
          label: "Compact and continue",
          action: "compact_continue",
        },
        secondaryAction: {
          label: "Fork into new task",
          action: "fork_new_task",
        },
        tertiaryAction: { label: "New task", action: "new_task" },
      };
    case "rate_limited": {
      const resetsAt = outcome.rateLimitInfo?.resetsAt;
      const resetsWhen = resetsAt ? formatResetWhen(resetsAt) : null;
      // A plan window (five_hour / seven_day / overage) is the USER'S own plan at
      // its limit, not our service being busy — say whose limit it is. Note this
      // card is OUTSIDE the founder's scoped "Claude" exception (engine picker and
      // sign-in step only), so it says "your plan".
      const planLimit = Boolean(outcome.rateLimitInfo?.rateLimitType);
      return {
        severity: "info",
        title: planLimit ? "Plan limit reached" : "Momentarily paused",
        description: planLimit
          ? resetsWhen
            ? `Your plan is at its limit. It works again ${resetsWhen}.`
            : "Your plan is at its limit. It works again once the window resets."
          : resetsWhen
            ? `The AI service is busy right now. Your request will work again ${resetsWhen}.`
            : "The AI service is busy right now. Your request will work again in a moment.",
        primaryAction: { label: "Try again", action: "retry" },
      };
    }
    case "image_error":
      return {
        severity: "warning",
        title: "Couldn't read an image",
        description:
          outcome.message ||
          "One of the attached images couldn't be processed. Try re-uploading or removing it.",
        primaryAction: { label: "Try again", action: "retry" },
      };
    case "request_invalid":
      /* Deliberately does NOT surface `outcome.message`. Its neighbours do,
         because a provider's prose about an image is readable; this one says
         "messages.19.content.1: `thinking` blocks ... cannot be modified", which
         tells a non-technical user nothing and reads like their fault. The honest
         summary is that the history could not be sent, and that trying again
         repairs it — the request builder drops the unsendable part on the next
         attempt rather than replaying it. */
      return {
        severity: "error",
        title: "Couldn't send this conversation",
        description:
          "Something in this task's history couldn't be sent to the model. Trying again rebuilds it. If it keeps happening, starting a new task will clear it.",
        primaryAction: { label: "Try again", action: "retry" },
      };
    case "model_error":
      return {
        severity: "error",
        title: "Service temporarily unavailable",
        description:
          outcome.message ||
          "Vestra is having trouble reaching its AI provider. We have been notified. Please try again in a few minutes.",
        primaryAction: { label: "Try again", action: "retry" },
        secondaryAction: { label: "Contact support", action: "support" },
      };
    case "model_unavailable":
      return {
        severity: "error",
        title: "Model unavailable",
        description:
          outcome.message ||
          "The selected model isn't available right now. Try again or pick a different model.",
        primaryAction: { label: "Try again", action: "retry" },
        secondaryAction: { label: "Contact support", action: "support" },
      };
    case "auth_error":
      // The default copy blames US, which is right for a Vestra-side credential
      // fault and FALSE on an engine running the user's own plan: nothing is wrong
      // on our side, their account simply needs connecting. When the engine knows
      // that, it sends its own sentence and it leads — and support is dropped with
      // it, because there is nothing on our side for support to fix.
      return {
        severity: "error",
        title: outcome.message ? "Sign-in needed" : "Authentication issue",
        description:
          outcome.message ||
          "There was a problem authenticating with the AI service. This is on our end. Please try again or reach out.",
        primaryAction: { label: "Try again", action: "retry" },
        ...(outcome.message
          ? {}
          : {
              secondaryAction: { label: "Contact support", action: "support" },
            }),
      };
    case "billing_error":
      // Two accounts can run dry, and the card must say whose: the TYPED owner says,
      // never the presence of a message. The person's OWN plan (owner 'person'): the
      // engine's sentence leads and support is dropped, since nothing on our side needs
      // fixing. Otherwise the credit that ran out is OURS, the key behind this model's
      // provider: no wait brings it back, so the card says that, says we know, and
      // points at the move that works now (another model). Never "busy, try again in
      // a few minutes": a Google 402 once read exactly that (staging 2026-10-01).
      return outcome.owner === "person"
        ? {
            severity: "error",
            title: "Your plan needs attention",
            description:
              outcome.message ||
              "Your own plan's account needs attention before this can run. Fix it there, then try again.",
            primaryAction: { label: "Try again", action: "retry" },
          }
        : {
            severity: "error",
            title: "This model is out of credit",
            description:
              "Our account with this model's provider has run out of credit. This is on our side, not yours, and the team has been alerted. Pick another model and try again.",
            primaryAction: { label: "Try again", action: "retry" },
            secondaryAction: { label: "Contact support", action: "support" },
          };
    case "network_error":
      // Transport-layer drop (Anthropic API socket closed mid-stream). The
      // already-produced output is preserved in the transcript; retry picks
      // up from there. Phrasing avoids blaming the user's network — the
      // observed source is the upstream HTTPS stream, not the client side.
      return {
        severity: "warning",
        title: "Connection dropped",
        description:
          "The AI service connection dropped before the response finished. The work above is saved. Try again to pick up where it left off.",
        primaryAction: { label: "Try again", action: "retry" },
      };
    case "unknown": {
      // Classify on the `unknown` branch so structural failures (e.g. a
      // Composio 400 saying "This operation is not supported for this
      // document") don't get the "Try again" treatment — retrying a
      // deterministic failure burns credits without any chance of success.
      // See docs/learnings/2026-04-30-oauth-resume-and-lifecycle-disease.md.
      //
      // A desktop message the server stopped waiting for because the person's
      // Mac never came back (server task-inbox give-up). No "Try again": a resend
      // from here runs on the cloud box, and a desktop message never falls back
      // to the cloud. Opening Vestra on the Mac is the way forward, and the
      // server's sentence says so.
      if (code === "undelivered_machine") {
        return {
          severity: "warning",
          title: "Didn't reach your Mac",
          description:
            errorText ||
            "Your Mac wasn't connected, so this message never ran. Open Vestra on your Mac and send it again.",
        };
      }
      // An ANSWER whose delivery to the task gave up (server gate-refusal,
      // refuseForUndeliveredAnswer). No "Try again": it resends the last TYPED
      // message, which here is the request the task already ran, never the
      // answer. The server's sentence says to send the answer as a message.
      if (code === "answer_undelivered") {
        return {
          severity: "warning",
          title: "Your answer didn't go through",
          description:
            errorText ||
            "Your answer didn't reach the task, so it stopped here. Send it again as a message to keep going.",
        };
      }
      const category = classifyError(code, errorText);
      if (category === "structural") {
        return {
          severity: "error",
          title: "This request can't be retried",
          description:
            errorText ||
            "The request was rejected and won't succeed on retry. Edit your request or contact support.",
          primaryAction: {
            label: "Edit your request",
            action: "fork_new_task",
          },
          secondaryAction: { label: "Contact support", action: "support" },
        };
      }
      return {
        severity: "error",
        title: "Something went wrong",
        description:
          errorText ||
          "Vestra ran into an unexpected issue. This is usually temporary.",
        primaryAction: { label: "Try again", action: "retry" },
        secondaryAction: { label: "Contact support", action: "support" },
      };
    }
  }
}

/**
 * Does this TurnOutcome resolve to an ACTIONABLE ErrorBlock variant — a card
 * with a meaningful title/CTA the user can act on (max_budget→Recharge,
 * max_turns→Continue, hook_blocked→Continue, refused-no-text→declined, every
 * failure kind, …)? `detached` is the one variant with a title but no CTA; it
 * is reachable here only in principle, since its status:'failed' means the
 * caller's `turnFailed` gate already fired and this is never consulted.
 *
 * PR-ERR-3 follow-up: streamingHandlers uses this to route an EMPTY terminal
 * turn. A turn that ends empty but carries an actionable reason must render
 * THAT card (with its CTA) — not the generic "no output" affordance, which
 * would drop the affordance (e.g. the Recharge-credits button on an empty
 * out-of-credits turn). The generic no_output line is shown ONLY when this
 * returns false (a genuinely reasonless empty completion).
 *
 * Returns false for the kinds that intentionally render NO card on an empty
 * turn: `completed`/`deferred` (getOutcomeVariant already returns null), and a
 * `refused` WITH text (the text renders as the reply). The caller is
 * additionally responsible for excluding `user_aborted` (a user Stop shows no
 * card) and HITL pauses — see streamingHandlers.
 */
export function hasActionableOutcomeVariant(outcome: TurnOutcome): boolean {
  return getOutcomeVariant(outcome, "", undefined) !== null;
}

/**
 * Does this outcome KIND's card offer a plain resend ("Try again") as its
 * primary action? The turn footer's Retry affordance reads this so it never
 * offers a retry the card itself would refuse to offer — `context_exceeded`
 * routes to Compact/Fork, not a resend that would overflow again.
 *
 * Derived from `getOutcomeVariant` (the single variant source) against a
 * minimal outcome, so it can never drift from the cards. `code` is the
 * block's own code when there is one: an `unknown` failure can carry a code
 * whose card offers no resend (a Mac that never came back, an answer that
 * never reached the task).
 */
export function outcomeKindOffersRetry(
  kind: TurnOutcome["kind"],
  code?: string
): boolean {
  const variant = getOutcomeVariant(
    { kind, raw: { subtype: "", errors: [] } } as unknown as TurnOutcome,
    "",
    code
  );
  return variant?.primaryAction?.action === "retry";
}

/**
 * Legacy variant resolver. Consulted only when `outcome` is absent
 * (in-flight transcripts from before Batch 2). No new branches should
 * be added here — extend `getOutcomeVariant` and `TurnOutcome.kind`.
 */
function getLegacyVariant(category?: string): ErrorVariant {
  switch (category) {
    case "error_max_turns":
      return {
        severity: "warning",
        title: "Turn limit reached",
        description:
          "This task hit its step limit. The agent can pick up right where it left off.",
        primaryAction: { label: "Keep going", action: "continue" },
        secondaryAction: { label: "That's enough", action: "stop" },
      };
    case "error_max_budget_usd":
      // Legacy variant — no `triggeredBy` available. Match the credits-only
      // production reality today (see resolvePrewarmArgs).
      return {
        severity: "warning",
        title: "Out of credits",
        description:
          "You've used up your available credits and the agent paused mid-task. Top up to keep going.",
        primaryAction: { label: "Recharge credits", action: "credits" },
        secondaryAction: { label: "That's enough", action: "stop" },
      };
    case "credit_exhausted":
      return {
        severity: "error",
        title: "Service temporarily unavailable",
        description:
          "Vestra is having trouble reaching its AI provider. We have been notified. Please try again in a few minutes.",
        primaryAction: { label: "Try again", action: "retry" },
        secondaryAction: { label: "Contact support", action: "support" },
      };
    case "api_credit_error":
      return {
        severity: "error",
        title: "Service temporarily unavailable",
        description:
          "Vestra is having trouble reaching its AI provider. We have been notified. Please try again in a few minutes.",
        primaryAction: { label: "Try again", action: "retry" },
        secondaryAction: { label: "Contact support", action: "support" },
      };
    case "rate_limited":
      return {
        severity: "info",
        title: "Momentarily paused",
        description:
          "The AI service is busy right now. Your request will work again in a moment.",
        primaryAction: { label: "Try again", action: "retry" },
      };
    case "context_too_long":
      return {
        severity: "warning",
        title: "Conversation too long",
        description:
          "This conversation exceeded the context window. Start a new task to continue fresh.",
        primaryAction: { label: "Start new task", action: "new_task" },
        secondaryAction: { label: "Contact support", action: "support" },
      };
    case "auth_error":
      return {
        severity: "error",
        title: "Authentication issue",
        description:
          "There was a problem authenticating with the AI service. This is on our end. Please try again or reach out.",
        primaryAction: { label: "Try again", action: "retry" },
        secondaryAction: { label: "Contact support", action: "support" },
      };
    case "structured_output_error":
      return {
        severity: "warning",
        title: "Formatting issue",
        description:
          "The agent had trouble formatting its response. Try rephrasing your request.",
        primaryAction: { label: "Try again", action: "retry" },
      };
    case "empty_response":
      return {
        severity: "error",
        title: "No response",
        description:
          "The agent couldn't generate a response. This is usually a temporary service issue.",
        primaryAction: { label: "Try again", action: "retry" },
        secondaryAction: { label: "Contact support", action: "support" },
      };
    default:
      return {
        severity: "error",
        title: "Something went wrong",
        description:
          "Vestra ran into an unexpected issue. This is usually temporary.",
        primaryAction: { label: "Try again", action: "retry" },
        secondaryAction: { label: "Contact support", action: "support" },
      };
  }
}

function resolveVariant(
  outcome: TurnOutcome | undefined,
  category: string | undefined,
  errorText: string,
  code: string | undefined,
  agentMonthTitle?: string
): ErrorVariant | null {
  if (outcome) {
    return getOutcomeVariant(outcome, errorText, code, agentMonthTitle);
  }
  // Legacy path (in-flight transcripts from before Batch 2): if we have a
  // code/error pairing that classifies as structural and the legacy category
  // is the catch-all 'generic', upgrade to the structural variant. Without
  // this, AGENT_ERROR events with no `outcome` (e.g. relay-emitted error
  // without outcome wiring) would still show "Try again" for 400s.
  if (
    (!category || category === "generic") &&
    classifyError(code, errorText) === "structural"
  ) {
    return {
      severity: "error",
      title: "This request can't be retried",
      description:
        errorText ||
        "The request was rejected and won't succeed on retry. Edit your request or contact support.",
      primaryAction: { label: "Edit your request", action: "fork_new_task" },
      secondaryAction: { label: "Contact support", action: "support" },
    };
  }
  return getLegacyVariant(category);
}

export const ErrorBlockComponent = memo(function ErrorBlockComponent({
  error,
  code,
  category,
  outcome,
  taskId,
}: ErrorBlockProps) {
  const [resolved, setResolved] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  // P4 — card-owned task id. Prefer the id threaded from the render chain;
  // when genuinely absent (direct render without the renderer), fall back to
  // the id selected AT MOUNT. Reading selectedTaskId at click time (like the
  // legacy 'continue' handler does) would target the wrong task if the user
  // navigated between the card rendering and the click.
  const mountTaskIdRef = useRef(
    taskId ?? useTaskStore.getState().selectedTaskId
  );
  const ownTaskId = taskId ?? mountTaskIdRef.current;
  // A spent month's title names the agent of the same task the actions use.
  const agentMonthTitle = useAgentMonthTitle(ownTaskId);
  const variant = resolveVariant(
    outcome,
    category,
    error,
    code,
    agentMonthTitle
  );

  // P4 — out-of-credits resume. The credit store receives live
  // CREDIT_UPDATE / CREDIT_SUMMARY pushes; when the balance transitions
  // ≤0 → >0 while this card is mounted, surface a "Resume task" action that
  // continues the SAME task. Non-credit variants select a constant so they
  // never re-render on credit ticks; gating on isLoaded keeps the pre-load
  // placeholder balance (0) from arming the transition on old transcripts.
  const isCreditsCard = variant?.primaryAction?.action === "credits";
  const balance = useCreditStore((s) =>
    isCreditsCard && s.isLoaded ? s.balance : null
  );
  const [resumeReady, setResumeReady] = useState(false);
  const sawExhaustedRef = useRef(false);
  useEffect(() => {
    if (balance === null) return;
    if (balance <= 0) {
      sawExhaustedRef.current = true;
    } else if (sawExhaustedRef.current) {
      setResumeReady(true);
    }
  }, [balance]);

  const handleAction = useCallback(
    (action: ActionType) => {
      if (resolved) return;

      switch (action) {
        case "retry": {
          setResolved("Retrying...");
          // Resend the last user message — the ONE retry path, shared with the
          // turn footer's Retry affordance (lib/retry-turn.ts). Nothing to
          // resend → focus the composer so the user can retype.
          if (!resendLastUserMessage(useTaskStore.getState().selectedTaskId))
            focusComposer();
          break;
        }
        case "settings": {
          posthog.capture(ANALYTICS_EVENTS.UPGRADE_CTA_CLICKED, {
            source: "task_error_block",
          });
          setResolved("Opening settings...");
          window.location.href = "/account/plan";
          break;
        }
        case "guardrails": {
          // The per-turn limit lives with the credit thresholds on the settings
          // page — NOT /account/plan, where the 'settings' action goes.
          setResolved("Opening settings...");
          window.location.href = "/account/settings";
          break;
        }
        case "agent_budget": {
          // The agent's monthly budget lives in Settings → Agent budgets.
          setResolved("Opening settings...");
          window.location.href = `/account/settings#${AGENT_BUDGETS_ANCHOR}`;
          break;
        }
        case "credits": {
          // Open in a NEW TAB and keep this card mounted and unresolved: the
          // user tops up over there, the CREDIT_UPDATE push lands here, and
          // "Resume task" appears in place. The old same-tab navigation tore
          // down the task view, killing any chance of resuming this task.
          window.open("/account/credits", "_blank");
          break;
        }
        case "resume": {
          const socket = useConnectionStore.getState().getSocket();
          if (!socket || !ownTaskId) return;
          // One-shot: setResolved gates handleAction re-entry and hides the
          // actions row, so later CREDIT_UPDATE pushes or a second click can
          // never double-fire the continue.
          setResolved("Resuming...");
          useTaskStore.getState()._mutateTasks((tasks) => {
            const t = tasks.get(ownTaskId);
            if (t) t.phase = "waiting";
          });
          // Same server path as the checkpoint card — the server recomputes
          // maxBudgetUsd from the fresh balance, so taskId is the whole payload.
          socket.emit("checkpoint:continue", { taskId: ownTaskId });
          break;
        }
        case "continue": {
          setResolved("Continuing...");
          const socket = useConnectionStore.getState().getSocket();
          const taskId = useTaskStore.getState().selectedTaskId;
          if (!socket || !taskId) return;
          useTaskStore.getState()._mutateTasks((tasks) => {
            const t = tasks.get(taskId);
            if (t) t.phase = "waiting";
          });
          socket.emit("checkpoint:continue", { taskId });
          break;
        }
        case "stop": {
          setResolved("Stopped");
          break;
        }
        case "new_task": {
          window.location.href = "/task";
          break;
        }
        case "compact_continue": {
          // Inject /compact as the next user message in this session — SDK's
          // manual-compact primitive. If the session is still alive (we had a
          // terminal result but the persistent Query stays across turns in
          // our architecture), this runs compaction and continues.
          setResolved("Compacting…");
          const taskStore = useTaskStore.getState();
          taskStore.sendMessage("/compact");
          break;
        }
        case "fork_new_task": {
          // Hand off to the /task page with a fork-hint. The page listens for
          // the `fork_from` query param and uses SDK's forkSession to create
          // a sibling task with the original's state preserved. Full backend
          // wiring lives in its own small PR; today this redirects.
          setResolved("Forking…");
          const taskId = useTaskStore.getState().selectedTaskId;
          if (taskId) {
            window.location.href = `/task?fork_from=${encodeURIComponent(
              taskId
            )}`;
          } else {
            window.location.href = "/task";
          }
          break;
        }
        case "support": {
          const taskId = useTaskStore.getState().selectedTaskId;
          const params = new URLSearchParams({
            ctx: "error",
            ...(outcome?.kind && { kind: outcome.kind }),
            ...(category && { category }),
            ...(taskId && { task_id: taskId }),
            ...(error && { error: error.slice(0, 200) }),
            timestamp: new Date().toISOString(),
          });
          window.location.href = `/support?${params}`;
          break;
        }
      }
    },
    [resolved, category, outcome, error, ownTaskId]
  );

  if (!variant) return null;

  // P4 — once the balance is back, the credits card re-arranges around the
  // resume: "Resume task" leads, recharge stays available, stop trails.
  const effectiveVariant: ErrorVariant =
    isCreditsCard && resumeReady
      ? {
          ...variant,
          description:
            "Credits added. Resume to pick up right where the agent left off.",
          primaryAction: { label: "Resume task", action: "resume" },
          secondaryAction: { label: "Recharge credits", action: "credits" },
          tertiaryAction: variant.secondaryAction,
        }
      : variant;

  // A variant with no actions at all (`detached`) renders as a pure notice — the
  // row would otherwise be an empty strip of padding under the description.
  const hasActions = Boolean(
    effectiveVariant.primaryAction ||
    effectiveVariant.secondaryAction ||
    effectiveVariant.tertiaryAction
  );

  const description = effectiveVariant.description;
  const isLong = description.length > DESCRIPTION_TRUNCATE_AT;
  const visibleDescription =
    isLong && !expanded
      ? description.slice(0, DESCRIPTION_TRUNCATE_AT).trimEnd() + "…"
      : description;

  return (
    <div
      className="rounded-10 border border-border-default bg-bg-elevated overflow-hidden my-3 transition-style-error-block-inline"
      style={{ opacity: resolved ? 0.6 : 1 }}
    >
      <div className="px-4 py-3.5">
        <div className="flex items-center gap-2 text-4 font-semibold text-fg-default leading-tight">
          <ErrorIcon severity={effectiveVariant.severity} />
          {effectiveVariant.title}
        </div>
        <p className="text-4 text-fg-muted mt-1.5 pl-6 leading-relaxed whitespace-pre-wrap break-words">
          {visibleDescription}
          {isLong && (
            <>
              {" "}
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                className="underline text-3.5 text-fg-faint hover:text-fg-muted transition-colors"
              >
                {expanded ? "Show less" : "Show more"}
              </button>
            </>
          )}
        </p>
        {code && (
          <div className="font-mono text-4 text-3 text-fg-faint mt-1 pl-6 opacity-70">
            code: {code}
          </div>
        )}
        {resolved && (
          <div className="flex items-center gap-1.5 mt-2 pl-6 text-3.5 text-fg-faint">
            <ResolvedCheckIcon />
            {resolved}
          </div>
        )}
        {!resolved && hasActions && (
          <div className="flex items-center gap-2 mt-3 pl-6 flex-wrap">
            {effectiveVariant.primaryAction && (
              <button
                onClick={() =>
                  handleAction(effectiveVariant.primaryAction!.action)
                }
                className="px-3.5 py-1.5 rounded-12 bg-fg-default text-bg-base text-4 font-medium hover:opacity-85 transition-opacity"
              >
                {effectiveVariant.primaryAction.label}
              </button>
            )}
            {effectiveVariant.secondaryAction && (
              <button
                onClick={() =>
                  handleAction(effectiveVariant.secondaryAction!.action)
                }
                className="px-3.5 py-1.5 rounded-12 border border-border-default text-fg-muted text-4 font-medium hover:border-fg-faint transition-colors"
              >
                {effectiveVariant.secondaryAction.label}
              </button>
            )}
            {effectiveVariant.tertiaryAction && (
              <button
                onClick={() =>
                  handleAction(effectiveVariant.tertiaryAction!.action)
                }
                className="px-3.5 py-1.5 rounded-12 border border-border-default text-fg-muted text-4 font-medium hover:border-fg-faint transition-colors"
              >
                {effectiveVariant.tertiaryAction.label}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
